import { handleFetchPpSummaryAPI } from '@/api/storeManagement/history';
import { fetchStoreDetailAPI } from '@/api/stores';
import { useAppContext } from '@/provider/contexts';
import { PpSummary } from '@/types/pages/storeManagement/history';
import { Download } from '@mui/icons-material';
import { Box, CircularProgress, Typography, Divider, Tooltip, IconButton } from '@mui/material';
import dayjs from 'dayjs';
import saveAs from 'file-saver';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

const BLOCKED_LIST_GRID = 'minmax(110px, 0.6fr) minmax(0, 1.25fr)';
const VISIBLE_BLOCKED_COUNT = 10;

type SummaryCardItem = {
    title: string;
    value: number;
};

type BlockedItem = {
    barcode: string;
    reason: string;
};

const SummaryCard = ({ title, value }: SummaryCardItem) => (
    <Box
        sx={{
            width: '100%',
            maxWidth: 180,
            minWidth: 0,
            px: 1.25,
            py: 1,
            borderRadius: 1.5,
            border: (theme) => `1px solid ${theme.palette.divider}`,
            bgcolor: 'background.paper',
        }}
    >
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
            {title}
        </Typography>
        <Typography
            variant="h6"
            sx={{
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                fontSize: 'clamp(1rem, 1.8vw, 1.25rem)',
                lineHeight: 1.25,
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
            }}
        >
            {value}
        </Typography>
    </Box>
);

const BlockedListColumnHeader = ({ title }: { title: string }) => (
    <Typography variant="caption" sx={{ fontWeight: 700 }}>
        {title}
    </Typography>
);

const BlockedListRow = ({ item, showBorder }: { item: BlockedItem; showBorder: boolean }) => (
    <Box
        sx={{
            display: 'grid',
            gridTemplateColumns: BLOCKED_LIST_GRID,
            columnGap: 2,
            px: 1.5,
            py: 1,
            alignItems: 'start',
            borderBottom: showBorder ? (theme) => `1px solid ${theme.palette.divider}` : 'none',
        }}
    >
        <Typography
            variant="body2"
            sx={{
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                fontVariantNumeric: 'tabular-nums',
            }}
            title={item.barcode}
        >
            {item.barcode}
        </Typography>
        <Typography
            variant="body2"
            sx={{
                whiteSpace: 'normal',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
                color: 'text.secondary',
            }}
            title={item.reason}
        >
            {item.reason}
        </Typography>
    </Box>
);

const BlockedListMoreFooter = ({
    hiddenBlockedCount,
    exportLoading,
    onDownload,
    t,
}: {
    hiddenBlockedCount: number;
    exportLoading: boolean;
    onDownload: () => void;
    t: (key: string, options?: Record<string, unknown>) => string;
}) => (
    <Box
        sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            px: 1.5,
            py: 1,
            bgcolor: 'action.hover',
        }}
    >
        <Typography variant="body2" color="text.secondary">
            {t('... and {{count}} more', {
                count: hiddenBlockedCount,
            })}
        </Typography>
        <Tooltip title={t('Download all blocked products')}>
            <span>
                <IconButton size="small" onClick={onDownload} disabled={exportLoading}>
                    {exportLoading ? <CircularProgress size={16} /> : <Download fontSize="small" />}
                </IconButton>
            </span>
        </Tooltip>
    </Box>
);

const PpContent = ({ jobId }: { jobId: string }) => {
    const { t } = useTranslation('common');
    const { manageStoreId, setNotification } = useAppContext();
    const [exportLoading, setExportLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [summaryInfo, setSummaryInfo] = useState<PpSummary>({} as PpSummary);

    useEffect(() => {
        const initialize = async () => {
            try {
                setLoading(true);
                const {
                    data: { data, isSuccess, message },
                } = await handleFetchPpSummaryAPI({
                    store_id: manageStoreId as number,
                    job_id: jobId,
                });
                if (isSuccess) {
                    setSummaryInfo(data);
                } else {
                    throw new Error(message);
                }
            } catch (error: unknown) {
                if (error instanceof Error) {
                    setNotification({ message: error?.message || t('Failed to Fetch PP Summary'), severity: 'error' });
                } else {
                    console.error('Failed to Fetch PP Summary:', error);
                }
            } finally {
                setLoading(false);
            }
        };

        initialize();
    }, [manageStoreId, jobId, setNotification, t]);

    const handleDownload = async () => {
        try {
            setExportLoading(true);
            const storeInfo = await getStoreDetails();
            const jsonString = JSON.stringify(summaryInfo.barcodes.blocked);
            const blob = new Blob([jsonString], {
                type: 'application/json',
            });

            const exportDate = dayjs(new Date()).format('YYYYMMDD');
            const exportTime = dayjs(new Date()).format('HHmmss');
            if (storeInfo) {
                saveAs(
                    blob,
                    `aura_hq_blocked_products_${storeInfo.store_name}_${storeInfo.store_code}_${exportDate}_${exportTime}.json`,
                );
            } else {
                saveAs(blob, `aura_products_${exportDate}_${exportTime}.json`);
            }

            setNotification({ message: t('File exported successfully.'), severity: 'success' });
        } catch {
            setNotification({ message: t('File export failed.'), severity: 'error' });
        } finally {
            setExportLoading(false);
        }
    };

    const getStoreDetails = async () => {
        if (!manageStoreId) return;
        const storeId = manageStoreId;

        const {
            data: { data, isSuccess },
        } = await fetchStoreDetailAPI(storeId);

        if (isSuccess) {
            return data.store_info;
        }
    };

    const summaryCards = [
        { title: t('Total products'), value: summaryInfo?.product_length ?? 0 },
        { title: t('Products updated'), value: summaryInfo?.updated ?? 0 },
        { title: t('Labels updated'), value: summaryInfo?.pushed_tags_count ?? 0 },
        { title: t('Products blocked'), value: summaryInfo?.blocked ?? 0 },
    ];
    const blocked = summaryInfo?.barcodes?.blocked ?? [];
    const visibleBlocked = blocked.slice(0, VISIBLE_BLOCKED_COUNT);
    const hasMoreBlocked = blocked.length > visibleBlocked.length;
    const hiddenBlockedCount = blocked.length - visibleBlocked.length;

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                width: '100%',
                maxWidth: 720,
                mx: 'auto',
                gap: 1.5,
            }}
        >
            {loading ? (
                <>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 200 }}>
                        <CircularProgress />
                    </div>
                </>
            ) : (
                <>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns: {
                                xs: 'repeat(2, minmax(0, 1fr))',
                                md: 'repeat(4, minmax(0, 1fr))',
                            },
                            gap: 1,
                        }}
                    >
                        {summaryCards.map(({ title, value }) => (
                            <Box
                                key={title}
                                sx={{
                                    width: '100%',
                                    maxWidth: { xs: '100%', md: 160 },
                                    justifySelf: 'center',
                                }}
                            >
                                <SummaryCard title={title} value={value} />
                            </Box>
                        ))}
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box
                        sx={{
                            border: (theme) => `1px solid ${theme.palette.divider}`,
                            borderRadius: 1.5,
                            overflow: 'hidden',
                            bgcolor: 'background.paper',
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                px: 1.5,
                                py: 1,
                                bgcolor: 'action.hover',
                            }}
                        >
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                {t('Blocked Products & Reasons')}
                            </Typography>
                        </Box>

                        {visibleBlocked.length === 0 ? (
                            <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 2 }}>
                                {t('None')}
                            </Typography>
                        ) : (
                            <>
                                <Box
                                    sx={{
                                        display: 'grid',
                                        gridTemplateColumns: BLOCKED_LIST_GRID,
                                        columnGap: 2,
                                        px: 1.5,
                                        py: 0.75,
                                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                                        bgcolor: 'background.default',
                                    }}
                                >
                                    <BlockedListColumnHeader title={t('Barcode')} />
                                    <BlockedListColumnHeader title={t('Reason')} />
                                </Box>

                                <Box sx={{ maxHeight: 320, overflowY: 'auto' }}>
                                    {visibleBlocked.map((it, idx) => (
                                        <BlockedListRow
                                            key={`${it.barcode}-${idx}`}
                                            item={it}
                                            showBorder={idx !== visibleBlocked.length - 1 || hasMoreBlocked}
                                        />
                                    ))}
                                </Box>

                                {hasMoreBlocked && (
                                    <BlockedListMoreFooter
                                        hiddenBlockedCount={hiddenBlockedCount}
                                        exportLoading={exportLoading}
                                        onDownload={handleDownload}
                                        t={t}
                                    />
                                )}
                            </>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default PpContent;
